import { cn } from '@/lib/utils'

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'ebt' | 'nonEbt'
  size?: 'sm' | 'md'
}

export function Badge({ className, variant = 'default', size = 'sm', children, ...props }: BadgeProps) {
  const variants = {
    default: 'bg-slate-700 text-slate-300',
    success: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    warning: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    danger: 'bg-red-500/20 text-red-400 border border-red-500/30',
    info: 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30',
    ebt: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    nonEbt: 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
  }

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm'
  }

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-md',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}

// Phase-specific badges
export function PhaseBadge({ phase }: { phase: 'INITIAL' | 'INTERMEDIATE' | 'ADVANCED' }) {
  const config = {
    INITIAL: { label: 'Initial', variant: 'info' as const },
    INTERMEDIATE: { label: 'Intermediate', variant: 'warning' as const },
    ADVANCED: { label: 'Advanced', variant: 'success' as const }
  }

  const { label, variant } = config[phase]
  return <Badge variant={variant}>{label}</Badge>
}

// Role badges
export function RoleBadge({ role }: { role: 'ADMIN' | 'INSTRUCTOR' | 'STUDENT' }) {
  const config = {
    ADMIN: { label: 'Admin', variant: 'danger' as const },
    INSTRUCTOR: { label: 'Instructor', variant: 'info' as const },
    STUDENT: { label: 'Student', variant: 'default' as const }
  }

  const { label, variant } = config[role]
  return <Badge variant={variant}>{label}</Badge>
}

// Status badges
export function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' }> = {
    SCHEDULED: { label: 'Scheduled', variant: 'info' },
    IN_PROGRESS: { label: 'In Progress', variant: 'warning' },
    COMPLETED: { label: 'Completed', variant: 'success' },
    CANCELLED: { label: 'Cancelled', variant: 'danger' },
    NOT_STARTED: { label: 'Not Started', variant: 'default' },
    NEEDS_REVIEW: { label: 'Needs Review', variant: 'warning' },
    ACTIVE: { label: 'Active', variant: 'success' },
    ARCHIVED: { label: 'Archived', variant: 'default' }
  }

  const { label, variant } = config[status] || { label: status, variant: 'default' }
  return <Badge variant={variant}>{label}</Badge>
}
